import type { ReviewTicker } from "@eu.mdotm.api/sphere";
import { useLocaleFormatters } from "$root/localization/hooks";
import { type TableColumn, BaseTable } from "@mdotm/shadcn/legacy/components";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { PrintableProps } from "../../configuration/types";
import type { CommonSetUpReportProps, UnionMapData } from "../../configuration/hooks/useExtractReports";
import { Card } from "../Card";
import { TinyTableDataCell, TinyTableHeadCell } from "../table/tiny-table";

type CompositionProps = CommonSetUpReportProps<UnionMapData["portfolio-reference"]>;

const Composition = ({ data, list }: PrintableProps<{ data: CompositionProps }, ReviewTicker>): React.JSX.Element => {
	const { t } = useTranslation();
	const { formatNumber } = useLocaleFormatters();

	const columns = useMemo<Array<TableColumn<ReviewTicker>>>(
		() => [
			{
				name: "instrument",
				minWidth: 248,
				header: (props) => <TinyTableHeadCell {...props}>{t("COMPOSITION.INSTRUMENT")}</TinyTableHeadCell>,
				content: ({ instrument }, props) => (
					<TinyTableDataCell fontSize={9} lineClamp={3} {...props}>
						{instrument ?? ""}
					</TinyTableDataCell>
				),
			},
			{
				name: "alias",
				width: 104,
				header: (props) => <TinyTableHeadCell {...props}>{t("COMPOSITION.ALIAS")}</TinyTableHeadCell>,
				content: ({ alias, proxyOverwriteType }, props) => (
					<TinyTableDataCell {...props}>
						{proxyOverwriteType === "PORTFOLIO_MIXED" ? "Portfolio" : (alias ?? "")}
					</TinyTableDataCell>
				),
			},
			{
				name: "assetClass",
				width: 85,
				header: (props) => <TinyTableHeadCell {...props}>{t("COMPOSITION.ASSET_CLASS")}</TinyTableHeadCell>,
				content: ({ assetClass, proxyOverwriteType }, props) => (
					<TinyTableDataCell {...props}>
						{proxyOverwriteType === "PORTFOLIO_MIXED" ? "Portfolio" : (assetClass ?? "")}
					</TinyTableDataCell>
				),
			},
			{
				name: "microAssetClass",
				width: 144,
				header: (props) => <TinyTableHeadCell {...props}>{t("COMPOSITION.MICRO_ASSET_CLASS")}</TinyTableHeadCell>,
				content: ({ microAssetClass, proxyOverwriteType }, props) => (
					<TinyTableDataCell fontSize={9} lineClamp={3} {...props}>
						{proxyOverwriteType === "PORTFOLIO_MIXED" ? "Portfolio" : (microAssetClass ?? "")}
					</TinyTableDataCell>
				),
			},
			{
				name: "weight",
				width: 56,
				header: (props) => <TinyTableHeadCell {...props}>{t("COMPOSITION.WEIGHT")}</TinyTableHeadCell>,
				cellClassName: "tabular-nums",
				content: ({ weight }, props) => (
					<TinyTableDataCell {...props}>{`${formatNumber(weight ?? 0)}%`}</TinyTableDataCell>
				),
				align: "end",
			},
		],
		[t, formatNumber],
	);

	return (
		<Card title="Composition">
			<BaseTable
				disableVirtualScroll
				palette="uniform"
				columns={columns}
				rows={list}
				noDataText={t("COMPOSITION.NO_COMPOSITION")}
			/>
		</Card>
	);
};

export default Composition;
